import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Megaphone } from 'lucide-react';
import { fetchReleaseNotes } from '../../api/releaseNotes';
import type { ReleaseNote } from '../../types';

/** "26 Tem" biçiminde kısa tarih. */
function formatShortDate(note: ReleaseNote): string {
  const raw = note.published_at || note.created_at;
  if (!raw) return '—';
  return new Date(raw).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Dashboard "Güncelleme Notları" kartı — yayınlanmış son 5 notu listeler.
 * Hata durumunda veya kayıt yoksa kart hiç gösterilmez (dashboard'u bozmaz).
 */
export function ReleaseNotesCard() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<ReleaseNote[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchReleaseNotes()
      .then((data) => {
        if (!cancelled) setNotes(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) return null;
  if (!loading && (!notes || notes.length === 0)) return null;

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="dashboard-release-notes">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-900">Güncelleme Notları</h3>
        </div>
        <button
          type="button"
          data-testid="dashboard-release-notes-all"
          onClick={() => navigate('/release-notes')}
          className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          Tümünü gör
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>

      {loading || !notes ? (
        <div className="space-y-2" aria-busy="true" data-testid="dashboard-release-notes-loading">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-5 w-14 animate-pulse rounded-full bg-slate-100" />
              <div className="h-4 flex-1 animate-pulse rounded bg-slate-100" />
              <div className="h-4 w-10 animate-pulse rounded bg-slate-100" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-1">
          {notes.slice(0, 5).map((note) => (
            <button
              key={note.id}
              type="button"
              data-testid={`dashboard-release-note-${note.id}`}
              onClick={() => navigate('/release-notes')}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition hover:bg-slate-50"
            >
              <span className="inline-flex shrink-0 items-center rounded-full bg-blue-100 px-2 py-0.5 font-mono text-xs font-semibold text-blue-700">
                v{note.version}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700" title={note.title}>
                {note.title}
              </span>
              <span className="shrink-0 text-xs text-slate-400">{formatShortDate(note)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
